/**
 * Global state management for the application.
 * This file contains the state interface, initial state, and context provider.
 */

import {
  CarbonProjectAnalysis,
  CarbonSiteAnalysis,
  Project,
  BiodiversityProjectAnalysis,
  BiodiversitySiteAnalysis,
  Site,
  EnvironmentalProjectAnalysis,
  EnvironmentalSiteAnalysis,
  UserRole,
  UserStatus,
  ProjectMapData,
  LocationAdminDetail,
  DeploymentAdminDetailBase,
  DeviceAdminDetail,
} from '../api/Api';
import { AdminState, initialAdminState } from './admin.state';

export interface User {
  first_name?: string;
  /** Last Name */
  last_name?: string;
  /**
   * Email
   * @format email
   */
  email: string;
  /** User Id */
  user_id: string;
  role: UserRole[];
  status: UserStatus;
  /** Is Email Verified */
  is_email_verified: boolean;
}

// Extended Project type that includes sites
export interface ProjectWithSites extends Project {
  sites?: Site[];
  is_data_room_enabled?: boolean;
}

export interface BioInteractiveMapFilterState {
  selectedSite?: Site;
  selectedLocation?: LocationAdminDetail;
  selectedDeployment?: DeploymentAdminDetailBase;
  selectedDevice?: DeviceAdminDetail;
  mapOpacity: number;
}

export interface State {
  selectedProject: {
    projectDetails: ProjectWithSites | undefined;
    carbonAnalysis?: CarbonProjectAnalysis | undefined;
    carbonSiteAnalysis?: CarbonSiteAnalysis[] | undefined;
    biodiversityProjectAnalysis?: BiodiversityProjectAnalysis | undefined;
    biodiversitySiteAnalysis?: BiodiversitySiteAnalysis[] | undefined;
    environmentalProjectAnalysis?: EnvironmentalProjectAnalysis | undefined;
    environmentalSiteAnalysis?: EnvironmentalSiteAnalysis[] | undefined;
    hasAnyCarbonSiteAnalysis?: boolean | undefined;
    hasAnyBiodiversitySiteAnalysis?: boolean | undefined;
    hasAnyEnvironmentalSiteAnalysis?: boolean | undefined;
    bioMapData?: ProjectMapData | undefined;
  };
  projects: ProjectWithSites[];
  selectedSite?: Site | undefined;
  user: User | undefined;
  admin: AdminState;
  bioInteractiveMapFilter: BioInteractiveMapFilterState;
}

// Initial state
export const initialState: State = {
  selectedProject: {
    projectDetails: undefined,
    carbonAnalysis: undefined,
    carbonSiteAnalysis: [],
    biodiversityProjectAnalysis: undefined,
    biodiversitySiteAnalysis: [],
    environmentalProjectAnalysis: undefined,
    environmentalSiteAnalysis: [],
  },
  projects: [],
  selectedSite: undefined,
  user: undefined,
  admin: initialAdminState,
  bioInteractiveMapFilter: {
    selectedSite: undefined,
    selectedLocation: undefined,
    selectedDeployment: undefined,
    selectedDevice: undefined,
    mapOpacity: 0.8,
  },
};